import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Building2, UserCircle2, Eye, CheckCircle2, Delete, Trash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { usePagination } from '../hooks/usePagination';
import { toast } from 'react-toastify';
import { Pagination } from '../utils/Pagination';
import debounce from 'lodash/debounce';
import { formatDate } from '../utils/Config';
import { deleteHealthFacility, fetchHealthFacilities } from '../redux/HealthFacilitiesSlice';
import { Oval } from 'react-loader-spinner';
import ImportFacilities from '../components/ImportFacilities';

const FACILITY_TYPES = ['All Types', 'Hospital', 'Clinic', 'CHPS'];
const REGIONS = [
    'All Regions',
    'Greater Accra',
    'Ashanti',
    'Western',
    'Eastern',
    'Central',
    'Northern',
    'Upper East',
    'Upper West',
    'Volta',
    'Bono'
];
const OWNERSHIP_TYPES = ['All Ownership', 'Government', 'Private', 'CHAG'];

const FacilitySearch = () => {
    const dispatch = useDispatch();
    const [searchTerm, setSearchTerm] = useState('');
    const { loading } = useSelector((state) => state.healthFacilities);

    const debouncedSearch = useCallback(
        debounce((term) => {
            dispatch(fetchHealthFacilities({
                page: 1,
                limit: 15,
                search: term,
                filter: '',
                filter_type: ''
            }));
        }, 500),
        [dispatch]
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedSearch(value);
    };

    useEffect(() => {
        return () => {
            debouncedSearch.cancel();
        };
    }, [debouncedSearch]);

    return (
        <div className="relative">
            <div className="relative">
                <Search
                    className={`absolute left-3 top-1/2 transform -translate-y-1/2 ${loading ? 'text-blue-500 animate-pulse' : 'text-gray-400'
                        }`}
                    size={20}
                />
                <input
                    type="text"
                    value={searchTerm}
                    onChange={handleSearchChange}
                    placeholder="Search facilities..."
                    className="pl-10 pr-4 py-2 w-full rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                />
            </div>
        </div>
    );
};

const FacilityFilters = ({ filters, onFilterChange }) => {
    return (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <select
                value={filters.type || 'All Types'}
                onChange={(e) => onFilterChange('type', e.target.value)}
                className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
                {FACILITY_TYPES.map(type => (
                    <option key={type} value={type}>
                        {type}
                    </option>
                ))}
            </select>

            <select
                value={filters.region || 'All Regions'}
                onChange={(e) => onFilterChange('region', e.target.value)}
                className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
                {REGIONS.map(region => (
                    <option key={region} value={region}>
                        {region}
                    </option>
                ))}
            </select>

            <select
                value={filters.ownership || 'All Ownership'}
                onChange={(e) => onFilterChange('ownership', e.target.value)}
                className="px-4 py-2 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
                {OWNERSHIP_TYPES.map(ownership => (
                    <option key={ownership} value={ownership}>
                        {ownership}
                    </option>
                ))}
            </select>
        </div>
    );
};

const FacilityTable = ({ facilities }) => {
    const navigate = useNavigate();
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)
    const dispatch = useDispatch()

    const handleView = (id) => {
        navigate(`/dashboard/health-facilities/${id}`);
    };

    const handleDelete = async (id) => {
        try {
            setIsDeleting(true);
            await dispatch(deleteHealthFacility(id)).unwrap();
            toast.success('Facility deleted successfully');
            setShowDeleteModal(false);
        } catch (error) {
            const errorMessage = error?.error || error?.message || 'Failed to delete facility';
            toast.error(errorMessage);
        } finally {
            setIsDeleting(false);
        }
    };

    return (
        <div className="overflow-x-auto">
            <table className="min-w-full bg-white rounded-lg overflow-hidden">
                <thead className="bg-gray-50">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Facility
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Region
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Ownership
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Date Added
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {facilities?.map((facility) => (
                        <tr key={facility.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 h-10 w-10">
                                        {facility?.image ? (
                                            <img
                                                src={facility.image}
                                                alt=""
                                                className="h-10 w-10 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                                                <Building2 className="h-6 w-6 text-gray-400" />
                                            </div>
                                        )}
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">
                                            {facility?.name}
                                        </div>
                                        <div className="text-sm text-gray-500 line-clamp-1">
                                            {facility?.address}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{facility?.type}</div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{facility?.region}</div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{facility?.ownership}</div>
                            </td>
                            <td className="px-6 py-4">
                                <div className="text-sm text-gray-900">{formatDate(facility?.created_at)}</div>
                            </td>

                            <td className="px-6 py-4 ">
                                <div className="flex gap-2 items-center">
                                    <button
                                        onClick={() => handleView(facility.id)}
                                        className="text-blue-600 hover:text-blue-900 hover:underline flex gap-1 items-center justify-center"
                                        title="View Details"
                                    >
                                        <Eye className="h-5 w-5" />
                                    </button>
                                    <button
                                        onClick={() => setShowDeleteModal(true)}
                                        className="text-red-600 hover:text-red-700"
                                        title="Delete Facility"
                                    >
                                        <Trash className="h-5 w-5" />
                                    </button>

                                </div>
                            </td>
                            {showDeleteModal && (
                                <div className="fixed inset-0 bg-black/50 flex items-center justify-center">
                                    <div className="bg-white rounded-lg p-6 w-full mx-6 lg:max-w-md">
                                        <h2 className="text-xl font-bold mb-4">
                                            Delete Facility
                                        </h2>
                                        <p>Are you sure you want to delete this facility?</p>
                                        <div className="flex justify-end gap-2 mt-4">
                                            <button
                                                onClick={() => setShowDeleteModal(false)}
                                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type='button'
                                                onClick={() => { handleDelete(facility.id) }}
                                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                                            >
                                                {isDeleting ? (
                                                    <div className=" flex justify-center items-center">
                                                        <Oval
                                                            visible={true}
                                                            height="20"
                                                            width="20"
                                                            color="#ffff"
                                                            ariaLabel="oval-loading"
                                                            wrapperStyle={{}}
                                                            wrapperClass=""
                                                        />
                                                    </div>
                                                ) : 'Confirm'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </tr>

                    ))}
                </tbody>

            </table>

        </div>
    );
};

const HealthFacility = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const facilities = useSelector(state => state.healthFacilities.items);
    const { loading, error, meta } = useSelector((state) => state.healthFacilities);
    const [showImportFileModal, setShowImportFileModal] = useState(false)




    const {
        pagination,
        handlePageChange,
        updatePaginationData,
        getPageNumbers,
        getItemRange
    } = usePagination({
        initialPage: meta.current_page || 1,
        initialPerPage: meta.per_page || 15
    });

    const [filters, setFilters] = useState({
        type: null,
        region: null,
        ownership: null
    });

    useEffect(() => {
        handleFetchFacilities();
    }, [pagination.currentPage, filters]);

    const handleFetchFacilities = () => {
        // Create an object for active filters
        const activeFilters = {};

        // Only include filters that are not null and not "All" options
        if (filters.type && filters.type !== 'All Types') {
            activeFilters['filter[type]'] = filters.type;
        }
        if (filters.region && filters.region !== 'All Regions') {
            activeFilters['filter[region]'] = filters.region;
        }
        if (filters.ownership && filters.ownership !== 'All Ownership') {
            activeFilters['filter[ownership]'] = filters.ownership;
        }

        dispatch(fetchHealthFacilities({
            page: pagination.currentPage,
            limit: pagination.perPage,
            activeFilters
        }))
            .unwrap()
            .then(response => {
                if (response.meta) {
                    updatePaginationData({
                        current_page: response.meta.current_page,
                        last_page: response.meta.last_page,
                        per_page: response.meta.per_page,
                        total: response.meta.total
                    });
                }
            })
            .catch((error) => {
                toast.error(error.message || 'Failed to fetch facilities');
            });
    };

    const handleFilterChange = (filterType, value) => {
        setFilters(prev => ({
            ...prev,
            [filterType]: value === `All ${filterType}s` ? null : value
        }));
        handlePageChange(1);
    };

    return (
        <div className="p-4">
            <div className="flex flex-col gap-2 sm:flex-row justify-between items-center mb-6">
                <h1 className="text-2xl font-semibold">Health Facilities</h1>
                <FacilitySearch />
            </div>

            <div className='flex flex-col gap-2'>
                <FacilityFilters
                    filters={filters}
                    onFilterChange={handleFilterChange}
                />
                <div className='flex justify-end gap-2 items-center'>
                    <button
                        onClick={() => navigate('/dashboard/add-health-facility')}
                        className='w-fit self-end font-lufga text-white bg-[#1330C5] hover:opacity-90 focus:outline-none rounded-md shadow-md text-base px-4 py-2 sm:px-10 sm:py-2 text-center'
                    >
                        Add Facility
                    </button>
                    <button
                        className='w-fit self-end font-lufga bg-gray-200 hover:opacity-90 focus:outline-none rounded-md shadow-md text-base px-4 py-2 sm:px-10 sm:py-2 text-center'
                        onClick={() => setShowImportFileModal(true)}
                    >Import csv file</button>
                </div>

            </div>

            {loading ? (
                <div className="flex justify-center items-center h-[80vh]">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500"></div>
                </div>
            ) : (
                <div className='my-6'>
                    <FacilityTable facilities={facilities} />
                </div>
            )}

            {!loading && facilities.length === 0 && (
                <div className="text-center py-4 text-gray-500">
                    No facilities found
                </div>
            )}

            <Pagination
                currentPage={pagination.currentPage}
                lastPage={pagination.lastPage}
                total={pagination.total}
                itemRange={getItemRange()}
                pageNumbers={getPageNumbers()}
                onPageChange={handlePageChange}
            />

            {showImportFileModal &&
                <ImportFacilities
                    onClose={() => { setShowImportFileModal(false) }}
                />}

        </div>
    );
};

export default HealthFacility;